import { StickyNote } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useCustomerActivities, useSalesLead } from '@/models/salesLead'
import { type Org } from '@/models/user'
import { ActivityCard } from '../Internal/SalesLeads/ActivityCard'
import { ActivityFilter } from '../Internal/SalesLeads/ActivityFilter'
import { type ActivityGroup, inGroups } from '../Internal/SalesLeads/activityGroups'
import { ActivityPages } from '../Internal/SalesLeads/ActivityPages'
import { ActivityTab } from '../Internal/SalesLeads/ActivityTab'
import { CustomerNoteComposer } from './CustomerNoteComposer'

/**
 * Everything that happened with the customer, newest first. With a sales lead it is the lead's
 * Activity tab, which carries the customer's events too; without one, the customer's own entries.
 */
export function CustomerTimeline({ org }: { org: Org }) {
  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-lg font-semibold">Timeline</h2>
      {org.sourceLeadId ? <LeadTimeline leadId={org.sourceLeadId} /> : <EventTimeline orgId={org.id} />}
    </section>
  )
}

function LeadTimeline({ leadId }: { leadId: string }) {
  const { data: lead } = useSalesLead(leadId)
  if (!lead) return <TimelineSkeleton />
  return <ActivityTab lead={lead} />
}

function EventTimeline({ orgId }: { orgId: string }) {
  const { data: activities } = useCustomerActivities(orgId)
  const [isWritingNote, setIsWritingNote] = useState(false)
  const [shownGroups, setShownGroups] = useState<ActivityGroup[]>([])
  if (!activities) return <TimelineSkeleton />
  return (
    <>
      {isWritingNote ? (
        <CustomerNoteComposer orgId={orgId} onClose={() => setIsWritingNote(false)} />
      ) : (
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            className="border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100 hover:text-amber-800"
            onClick={() => setIsWritingNote(true)}
          >
            <StickyNote className="mr-1 size-4" />
            Note
          </Button>
          {activities.length > 0 && (
            <ActivityFilter
              className="ml-auto"
              activities={activities}
              groups={shownGroups}
              onGroupsChange={setShownGroups}
            />
          )}
        </div>
      )}
      {activities.length === 0 ? (
        <Card>
          <CardContent>
            <p className="text-muted-foreground text-center">No activity yet</p>
          </CardContent>
        </Card>
      ) : (
        <ActivityPages
          // Keyed so another customer, or another filter, starts on its first page.
          key={`${orgId}:${shownGroups.join()}`}
          activities={inGroups(activities, shownGroups)}
          renderActivity={(activity) => <ActivityCard activity={activity} />}
        />
      )}
    </>
  )
}

function TimelineSkeleton() {
  return (
    <div className="flex flex-col gap-3">
      <Skeleton className="h-9 w-80" />
      <Skeleton className="h-24 w-full" />
      <Skeleton className="h-24 w-full" />
    </div>
  )
}
